/**
 * RightSidebar — full-height config panel that appears on the right side
 * when in editor-only mode. Shows the config/output for the selected
 * ::define block, giving more editing space than the floating popover.
 */

import type {
    UnitConfigContext,
} from "@magic-md/extension-sdk";
import type {
    ActionDeclaration,
    ConfigPanelContext,
} from "@magic-md/plugin-sdk";
import { JsonTree } from "@magic-md/plugin-sdk";
import { useT } from "@magic-md/i18n/react";
import {
    FilePenLine,
    GitBranchPlus,
    Loader2,
    PanelRightClose,
    Play,
    Puzzle,
    X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDefinitionUseBinding } from "../hooks/useDefinitionUseBinding";
import { useDocumentAssetReferences } from "../hooks/useDocumentAssetReferences";
import { useDraftConfig } from "../hooks/useDraftConfig";
import { usePluginUI } from "../hooks/usePluginUI";
import { attachDefinitionOutputs, buildConfigPanelUnits } from "../lib/config-panel-units";
import { collectParsedDocumentUseBindings } from "../lib/for-scope-units";
import { fileNameFromPath, pickerInitialPathFromDocument } from "../lib/path-utils";
import { getDocumentBundleDir } from "../lib/publish-utils";
import { runDefinitionWithFallback } from "../lib/run-definition";
import {
    collectScopeVariables,
    collectTemplateInputScopeVariables,
} from "../lib/scope-variables";
import {
    createLocalUseOverrideContent,
    isEditorTargetProvenanceCurrent,
    resolveImportedUnitSource,
    resolveSerializerTargetLocator,
    resolveUnitEditTarget,
    updateDefinitionConfigAndDropOutput,
    type ImportedUnitEditTarget,
} from "../lib/unit-edit-target";
import { ExtensionUnitConfigActions } from "../extensions/ExtensionContributions";
import { useExtensionUnitConfigActions } from "../extensions/useExtensionContributions";
import { getPlatformService } from "../platform";
import { useEditorStore } from "../store/editor-store";
import { usePluginExecutionStore } from "../store/plugin-execution-store";
import { getThemeAccentStyle } from "../theme/runtime/theme-accents";
import { showFilePickerDialog, showFolderPickerDialog } from "./dialogs";
import { PaneRenderProvider, usePaneContext } from "./PaneContext";
import {
    ConfigEditorModeButton,
    DefinitionPanelTabs,
    DirtyStateBanner,
    RawConfigEditor,
    SecondaryActionsBar,
    StatusDot,
} from "./shared";
import { UseBindEditor } from "./UseBindEditor";
import { UseWithEditor } from "./UseWithEditor";

const EMPTY_UNIT_CONFIG_CONTEXT: UnitConfigContext = {
    definitionId: "",
    plugin: "",
    config: {},
};

/* ── Resize constants ── */
const MIN_WIDTH = 300;
const MAX_WIDTH_VIEWPORT_RATIO = 0.8;
const DEFAULT_WIDTH = 360;

function getMaxPanelWidth() {
    if (typeof window === "undefined") return Number.MAX_SAFE_INTEGER;
    return Math.max(MIN_WIDTH, Math.floor(window.innerWidth * MAX_WIDTH_VIEWPORT_RATIO));
}

function clampPanelWidth(width: number) {
    return Math.min(getMaxPanelWidth(), Math.max(MIN_WIDTH, width));
}


function ImportedUnitEditNotice({
    target,
    busy,
    onOpenSource,
    onWriteSource,
    onCreateLocalUse,
    onDiscard,
}: {
    target: ImportedUnitEditTarget;
    busy: boolean;
    onOpenSource: () => void;
    onWriteSource: () => void;
    onCreateLocalUse: () => void;
    onDiscard: () => void;
}) {
    const t = useT("editor");
    return (
        <div
            className="mb-2 rounded-md border px-2.5 py-2 theme-accent-banner"
            style={getThemeAccentStyle("warning")}
        >
            <div className="flex items-center gap-2">
                <div className="flex min-w-0 flex-1 items-center gap-1 text-[10px] font-medium">
                    <span className="shrink-0 text-muted-foreground">{t("sidebar.importedFrom")}</span>
                    <button
                        type="button"
                        className="min-w-0 truncate text-left underline-offset-2 hover:underline"
                        onClick={onOpenSource}
                        title={target.sourceDocumentPath}
                    >
                        {fileNameFromPath(target.sourceDocumentPath)}
                    </button>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                    <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium theme-accent-icon-button"
                        style={getThemeAccentStyle("success")}
                        onClick={onCreateLocalUse}
                        title={t("sidebar.createLocalUseOverride")}
                    >
                        <GitBranchPlus size={10} />
                        {t("sidebar.localOverride")}
                    </button>
                    <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium theme-accent-icon-button"
                        style={getThemeAccentStyle("warning")}
                        disabled={busy}
                        onClick={onWriteSource}
                        title={t("sidebar.writeBackSource")}
                    >
                        {busy ? (
                            <Loader2 size={10} className="animate-spin" />
                        ) : (
                            <FilePenLine size={10} />
                        )}
                        {t("sidebar.sourceFile")}
                    </button>
                    <button
                        type="button"
                        className="rounded px-1.5 py-0.5 text-[10px] text-muted-foreground hover:bg-muted hover:text-foreground"
                        onClick={onDiscard}
                        title={t("sidebar.discardChanges")}
                    >
                        {t("sidebar.discardChanges")}
                    </button>
                </div>
            </div>
        </div>
    );
}

export function RightSidebar() {
    const rightSidebarOpen = useEditorStore((s) => s.rightSidebarOpen);
    if (!rightSidebarOpen) return null;
    return (
        <PaneRenderProvider>
            <RightSidebarInner />
        </PaneRenderProvider>
    );
}

function RightSidebarInner() {
    const t = useT("editor");
    const rightSidebarOpen = useEditorStore((s) => s.rightSidebarOpen);
    const definitionId = useEditorStore((s) => s.rightSidebarDefinitionId);
    const definitionLocator = useEditorStore(
        (s) => s.rightSidebarDefinitionLocator,
    );
    const definitionTarget = useEditorStore(
        (s) => s.rightSidebarDefinitionTarget,
    );
    const setRightSidebarDefinitionId = useEditorStore(
        (s) => s.setRightSidebarDefinitionId,
    );
    const toggleRightSidebar = useEditorStore((s) => s.toggleRightSidebar);
    const openFileFromPath = useEditorStore((s) => s.openFileFromPath);

    const [activeTab, setActiveTab] = useState<"config" | "output">("config");
    const [useRawEditor, setUseRawEditor] = useState(false);
    const [importedConfigBusy, setImportedConfigBusy] = useState(false);
    const previousDefinitionIdRef = useRef<string | null>(null);

    /* ── Resize state ── */
    const [panelWidth, setPanelWidth] = useState(DEFAULT_WIDTH);
    const isDragging = useRef(false);
    const startX = useRef(0);
    const startWidth = useRef(0);
    const handleMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            isDragging.current = true;
            startX.current = e.clientX;
            startWidth.current = clampPanelWidth(panelWidth);
            document.body.style.cursor = "col-resize";
            document.body.style.userSelect = "none";
        },
        [panelWidth],
    );

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isDragging.current) return;
            // Dragging left edge: moving left = wider (negative delta = wider)
            const delta = e.clientX - startX.current;
            setPanelWidth(clampPanelWidth(startWidth.current - delta));
        };
        const handleMouseUp = () => {
            if (!isDragging.current) return;
            isDragging.current = false;
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };
    }, []);

    const paneCtx = usePaneContext();
    const definitions = paneCtx.definitions;
    const definitionOutputs = paneCtx.definitionOutputs;
    const contentDefinitions = paneCtx.contentDefinitions;
    const importDocuments = paneCtx.importDocuments;
    const parsedDocument = paneCtx.parsedDocument;
    const documentPath = paneCtx.tab?.filePath ?? null;
    const provenanceMatches = isEditorTargetProvenanceCurrent(definitionTarget, {
        tabId: paneCtx.tabId,
        paneId: paneCtx.paneId,
        filePath: paneCtx.tab?.filePath,
    });
    const validatedDefinitionLocator = useMemo(() => {
        if (!definitionLocator) return undefined;
        if (!definitionLocator.fingerprint) return definitionLocator;
        if (
            !definitionTarget?.unitKind
            || !paneCtx.tab?.content
            || !provenanceMatches
            || !definitionId
        ) {
            return undefined;
        }
        return resolveSerializerTargetLocator({
            content: paneCtx.tab.content,
            unitKind: definitionTarget.unitKind,
            unitId: definitionId,
            locator: definitionLocator,
            fallbackOccurrence: definitionTarget.unitKind === "use" ? "first" : "last",
        });
    }, [
        definitionId,
        definitionLocator,
        definitionTarget,
        paneCtx.tab?.content,
        provenanceMatches,
    ]);
    const targetUsable = provenanceMatches
        && (!definitionLocator?.fingerprint || !!validatedDefinitionLocator);
    const selectedDefinitionId = targetUsable ? (definitionId ?? null) : null;
    const documentBundlePath = documentPath ? getDocumentBundleDir(documentPath) : null;
    const pickerInitialPath = pickerInitialPathFromDocument(paneCtx.tab?.filePath);
    const availableAssets = useDocumentAssetReferences(paneCtx.tab?.filePath, importDocuments);

    useEffect(() => {
        if (!targetUsable && definitionId) setRightSidebarDefinitionId(null);
    }, [definitionId, setRightSidebarDefinitionId, targetUsable]);
    const definitionsWithOutputs = useMemo(
        () => attachDefinitionOutputs(definitions, definitionOutputs),
        [definitions, definitionOutputs],
    );
    const availableUnits = useMemo(
        () => buildConfigPanelUnits({
            definitions: definitionsWithOutputs,
            definitionOutputs,
            forDefinitions: paneCtx.forDefinitions,
            contentDefinitions,
            imports: parsedDocument?.imports,
            uses: collectParsedDocumentUseBindings(parsedDocument),
            forExecutions: paneCtx.forExecutions,
            contentExecutions: paneCtx.contentExecutions,
            importExecutions: paneCtx.importExecutions,
            useExecutions: paneCtx.useExecutions,
        }),
        [
            definitionsWithOutputs,
            definitionOutputs,
            paneCtx.forDefinitions,
            contentDefinitions,
            parsedDocument,
            paneCtx.forExecutions,
            paneCtx.contentExecutions,
            paneCtx.importExecutions,
            paneCtx.useExecutions,
        ],
    );

    const executions = paneCtx.executions;
    const execution = useMemo(
        () => (selectedDefinitionId
            ? (executions.get(selectedDefinitionId) ?? null)
            : null),
        [executions, selectedDefinitionId],
    );
    const runPrimary = usePluginExecutionStore((s) => s.runPrimary);
    const runAction = usePluginExecutionStore((s) => s.runAction);
    const cancelExecution = usePluginExecutionStore((s) => s.cancelExecution);
    const clearExecution = usePluginExecutionStore((s) => s.clearExecution);

    const definition = selectedDefinitionId
        ? definitions.get(selectedDefinitionId)
        : undefined;
    const plugin = usePluginUI(definition?.pluginName);

    // Reset tab state when definition changes
    useEffect(() => {
        if (previousDefinitionIdRef.current === selectedDefinitionId) return;
        previousDefinitionIdRef.current = selectedDefinitionId;
        setActiveTab("config");
        setUseRawEditor(false);
    }, [selectedDefinitionId]);

    const {
        currentUseBinding,
        useSourceConfig,
        importSymbols,
        templateParams,
        templateInputSchema,
        missingRequiredTemplateInputs,
    } =
        useDefinitionUseBinding(
            selectedDefinitionId ?? undefined,
            parsedDocument ?? undefined,
            definitions,
            contentDefinitions,
            paneCtx.importExecutions,
            {
                content: paneCtx.tab?.content,
                locator: validatedDefinitionLocator,
            },
        );

    const unitEditTarget = useMemo(
        () => resolveUnitEditTarget({
            unitId: selectedDefinitionId,
            parsedDocument,
            documentPath,
            content: paneCtx.tab?.content,
            locator: validatedDefinitionLocator,
            sourceRange: currentUseBinding?.range,
        }),
        [
            selectedDefinitionId,
            parsedDocument,
            documentPath,
            paneCtx.tab?.content,
            validatedDefinitionLocator,
            currentUseBinding?.range,
        ],
    );
    const importedEditTarget =
        unitEditTarget?.kind === "imported-symbol" ? unitEditTarget : null;
    const editLocator = unitEditTarget?.kind === "local-definition"
        || unitEditTarget?.kind === "local-use"
        ? unitEditTarget.locator
        : validatedDefinitionLocator;

    const { draftConfig, isDirty, updateDraft, applyDraft, markClean, discardDraft } =
        useDraftConfig(
            selectedDefinitionId ?? undefined,
            definition?.config as Record<string, unknown> | undefined,
            {
                unitKind: currentUseBinding ? "use" : "define",
                ...(useSourceConfig ? {
                    sourceConfig: useSourceConfig,
                    bind: currentUseBinding?.bind,
                    with: currentUseBinding?.with,
                } : {}),
                locator: editLocator,
                sourceRange: currentUseBinding?.range,
            },
        );

    const runForAction = usePluginExecutionStore((s) => s.runForAction);
    const isTemplateUnit = currentUseBinding
        ? currentUseBinding.template === true || missingRequiredTemplateInputs.length > 0
        : !!definition?.template;

    const handleOpenImportedSource = useCallback(() => {
        if (!importedEditTarget) return;
        void openFileFromPath(importedEditTarget.sourceDocumentPath);
    }, [importedEditTarget, openFileFromPath]);

    const configCallSidecar = useCallback(
        async (
            sidecarId: string,
            method: string,
            params?: Record<string, unknown>,
        ) => getPlatformService().engine.sidecarCall(sidecarId, method, params),
        [],
    );

    const handleCreateLocalUseOverride = useCallback(() => {
        if (!importedEditTarget || !definition || !paneCtx.tab) return;
        const result = createLocalUseOverrideContent({
            content: paneCtx.tab.content,
            parsedDocument,
            target: importedEditTarget,
            draftConfig,
            sourceConfig: definition.config as Record<string, unknown>,
        });
        const editorState = useEditorStore.getState();
        editorState.setContentImmediate(result.content, paneCtx.tab.id);
        setRightSidebarDefinitionId(result.localUseId);
        editorState.setStatusMessage(t("sidebar.createdLocalOverride", { id: result.localUseId }));
    }, [
        importedEditTarget,
        definition,
        paneCtx.tab,
        parsedDocument,
        draftConfig,
        setRightSidebarDefinitionId,
        t,
    ]);

    const handleWriteImportedSourceConfig = useCallback(() => {
        if (!importedEditTarget || !definition || !documentPath || !parsedDocument) return;
        setImportedConfigBusy(true);
        void (async () => {
            const editorState = useEditorStore.getState();
            const engine = getPlatformService().engine;
            const readFile = async (filePath: string) => {
                const openTab = useEditorStore
                    .getState()
                    .tabs.find((tab) => tab.filePath === filePath);
                if (openTab) return openTab.content;
                return (await engine.workspaceReadFile({ path: filePath })).content;
            };
            try {
                const resolved = await resolveImportedUnitSource({
                    unitId: importedEditTarget.unitId,
                    parsedDocument,
                    documentPath,
                    readFile,
                });
                if (!resolved) {
                    throw new Error("Cannot resolve imported unit source");
                }

                const sourceContent = await readFile(resolved.sourceDocumentPath);
                const updated = updateDefinitionConfigAndDropOutput(
                    sourceContent,
                    resolved.sourceUnitId,
                    draftConfig,
                );
                if (updated === sourceContent) {
                    throw new Error(`Source definition not found: ${resolved.sourceUnitId}`);
                }

                await engine.workspaceWriteFile({
                    path: resolved.sourceDocumentPath,
                    content: updated,
                });
                const sourceTab = useEditorStore
                    .getState()
                    .tabs.find((tab) => tab.filePath === resolved.sourceDocumentPath);
                if (sourceTab) {
                    useEditorStore.getState().setExternalContent(updated, sourceTab.id);
                }
                markClean();
                editorState.setStatusMessage(
                    t("sidebar.wroteBackSource", { id: resolved.sourceUnitId }),
                );
                await usePluginExecutionStore
                    .getState()
                    .runImportAction(resolved.topLevelNamespace);
            } catch (err) {
                const message = err instanceof Error ? err.message : String(err);
                editorState.setStatusMessage(t("sidebar.writeBackFailed", { message }));
            } finally {
                setImportedConfigBusy(false);
            }
        })();
    }, [
        importedEditTarget,
        definition,
        documentPath,
        parsedDocument,
        draftConfig,
        markClean,
        t,
    ]);

    const handleRun = useCallback(() => {
        if (!definition || isTemplateUnit) return;

        if (importedEditTarget && isDirty) {
            useEditorStore
                .getState()
                .setStatusMessage(t("sidebar.requiresImportEditTarget"));
            return;
        }

        // ::use bindings (including those that materialize content templates)
        // use their own execution path — runPrimary expects a pluginName which
        // content-use definitions don't have.
        if (currentUseBinding) {
            if (isDirty) applyDraft();
            void usePluginExecutionStore.getState().runUseAction(
                currentUseBinding.id,
            );
            return;
        }

        void runDefinitionWithFallback({
            definition,
            isDirty,
            applyDraft,
            runPrimary,
            runForAction,
        });
    }, [
        definition,
        isDirty,
        applyDraft,
        runPrimary,
        runForAction,
        currentUseBinding,
        importedEditTarget,
        isTemplateUnit,
        t,
    ]);

    // Build ConfigPanelContext
    const configContext = useMemo<ConfigPanelContext>(
        () => ({
            currentDefinitionId: definition?.id ?? "",
            documentPath,
            documentBundlePath,
            selectFile: async (options) => {
                const result = await showFilePickerDialog({
                    accept: options?.accept,
                    initialPath: pickerInitialPath,
                });
                return result ?? "";
            },
            selectDirectory: async () => {
                const result = await showFolderPickerDialog({
                    initialPath: pickerInitialPath,
                });
                return result ?? "";
            },
            getAvailableDefinitions: () => definitionsWithOutputs,
            getAvailableUnits: () => availableUnits,
            getAvailableContentDefinitions: () => contentDefinitions,
            getAvailableImportDocuments: () => importDocuments,
            getAvailableAssets: () => availableAssets,
            previewAction: async (actionName: string) => {
                if (!definition) return { success: false, message: "No definition" };
                if (isTemplateUnit) {
                    return { success: false, message: t("instance.templateNotExecutable") };
                }
                return runAction(definition, actionName, definitions);
            },
            callSidecar: configCallSidecar,
            scopeVariables: definition
                ? [
                    ...collectTemplateInputScopeVariables(
                        definition.inputSchema,
                        definition.params,
                        draftConfig,
                    ),
                    ...collectScopeVariables(
                        definition.id,
                        parsedDocument?.blocks ?? [],
                        parsedDocument?.forDefinitions,
                        definitions,
                        { uses: collectParsedDocumentUseBindings(parsedDocument), contentDefinitions },
                    ),
                ]
                : undefined,
        }),
        [
            definitions,
            definitionsWithOutputs,
            availableUnits,
            contentDefinitions,
            importDocuments,
            definition,
            runAction,
            isTemplateUnit,
            draftConfig,
            parsedDocument,
            pickerInitialPath,
            availableAssets,
            documentPath,
            documentBundlePath,
            configCallSidecar,
            t,
        ],
    );
    const unitConfigContext = useMemo<UnitConfigContext>(
        () =>
            definition
                ? {
                      definitionId: definition.id,
                      plugin: definition.pluginName,
                      config: (definition.config ?? {}) as object,
                      output: execution?.output,
                  }
                : EMPTY_UNIT_CONFIG_CONTEXT,
        [definition, execution?.output],
    );
    const extensionUnitActions = useExtensionUnitConfigActions(unitConfigContext);

    if (!rightSidebarOpen) return null;

    const hasDefinition = !!definition;
    const hasPluginConfigPanel = !!plugin?.ConfigPanel;
    const showVisualConfig = hasPluginConfigPanel && !useRawEditor;
    const canRunUnit = !!plugin && !isTemplateUnit;

    return (
        <div
            className="flex flex-col h-full border-l border-border bg-background shrink-0 relative"
            style={{ width: clampPanelWidth(panelWidth) }}
        >
            {/* Resize handle (left edge) */}
            <button
                type="button"
                onMouseDown={handleMouseDown}
                className="absolute top-0 left-0 w-1 h-full cursor-col-resize hover:bg-primary/30 active:bg-primary/50 transition-colors z-10"
                aria-label={t("sidebar.resize")}
            />

            {/* ── Global sidebar header — neutral, just close button ── */}
            <div className="flex items-center gap-2 h-9 px-3 border-b border-border bg-muted/30 shrink-0">
                <span className="text-xs font-medium text-muted-foreground tracking-wide">
                    {t("sidebar.title")}
                </span>
                <div className="flex-1" />
                <button
                    type="button"
                    className="p-0.5 rounded hover:bg-muted text-muted-foreground"
                    onClick={toggleRightSidebar}
                    title={t("sidebar.close")}
                >
                    <PanelRightClose size={14} />
                </button>
            </div>

            {/* ── Config section — only when a definition is selected ── */}
            {hasDefinition ? (
                <div className="flex flex-col flex-1 min-h-0">
                    {/* Config section header with definition info + compact controls */}
                    <div className="flex items-center gap-1 h-10 px-2.5 border-b border-border bg-muted/15 shrink-0">
                        <StatusDot status={execution?.status ?? "idle"} />
                        <div className="min-w-0 flex-1">
                            <span
                                className="block text-[11px] font-medium text-foreground truncate"
                                title={t("definition.typeTitle", { type: definition.pluginName })}
                            >
                                {definition.id}
                            </span>
                        </div>
                        <DefinitionPanelTabs
                            activeTab={activeTab}
                            onChange={setActiveTab}
                            outputDuration={execution?.duration ?? undefined}
                            compact
                            configAccessory={
                                hasPluginConfigPanel ? (
                                    <ConfigEditorModeButton
                                        useRawEditor={useRawEditor}
                                        onChange={setUseRawEditor}
                                        compact
                                        embedded
                                    />
                                ) : undefined
                            }
                        />

                        {canRunUnit && execution?.status === "running" ? (
                            <button
                                type="button"
                                className="flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-medium transition-colors theme-accent-button shrink-0"
                                style={getThemeAccentStyle("error")}
                                onClick={() => cancelExecution(definition.id)}
                            >
                                <span className="flex items-center justify-center w-1.5 h-1.5 rounded-sm bg-background" />
                                {t("common.stop")}
                            </button>
                        ) : canRunUnit ? (
                            <button
                                type="button"
                                className="flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-medium transition-colors bg-primary text-primary-foreground hover:bg-primary/90 shrink-0"
                                onClick={handleRun}
                            >
                                <Play size={8} />
                                {t("common.run")}
                            </button>
                        ) : plugin && isTemplateUnit ? (
                            <span className="text-[9px] shrink-0 text-muted-foreground">
                                {t("definition.template")}
                            </span>
                        ) : (
                            <span
                                className="text-[9px] shrink-0 theme-accent-text"
                                style={getThemeAccentStyle("warning")}
                            >
                                {t("definition.notInstalled")}
                            </span>
                        )}

                        <button
                            type="button"
                            className="p-0.5 rounded hover:bg-muted text-muted-foreground"
                            onClick={() => setRightSidebarDefinitionId(null)}
                            title={t("sidebar.cancelSelection")}
                        >
                            <X size={12} />
                        </button>
                    </div>

                    {/* Content — fills remaining height */}
                    <div className="flex-1 min-h-0 overflow-auto">
                        {activeTab === "config" && (
                            <div className="p-3 space-y-2">
                                {isDirty && importedEditTarget && (
                                    <ImportedUnitEditNotice
                                        target={importedEditTarget}
                                        busy={importedConfigBusy}
                                        onOpenSource={handleOpenImportedSource}
                                        onWriteSource={handleWriteImportedSourceConfig}
                                        onCreateLocalUse={handleCreateLocalUseOverride}
                                        onDiscard={discardDraft}
                                    />
                                )}

                                {isDirty && !importedEditTarget && (
                                    <DirtyStateBanner
                                        onSave={applyDraft}
                                        onDiscard={discardDraft}
                                    />
                                )}

                                {currentUseBinding && (useSourceConfig || templateParams || templateInputSchema) && (
                                    <div className="space-y-2 mb-2">
                                        {useSourceConfig && (
                                            <UseBindEditor
                                                useBinding={currentUseBinding}
                                                definitions={definitions}
                                                sourceConfig={useSourceConfig}
                                                importSymbols={importSymbols}
                                                locator={editLocator}
                                            />
                                        )}
                                        {(templateParams || templateInputSchema) && (
                                            <UseWithEditor
                                                useBinding={currentUseBinding}
                                                templateParams={templateParams ?? []}
                                                inputSchema={templateInputSchema}
                                                definitions={definitions}
                                                locator={editLocator}
                                            />
                                        )}
                                    </div>
                                )}

                                {/* Plugin's ConfigPanel or raw JSON editor */}
                                {showVisualConfig && plugin?.ConfigPanel ? (
                                    <div>
                                        <plugin.ConfigPanel
                                            value={draftConfig}
                                            onChange={updateDraft}
                                            context={configContext}
                                        />
                                    </div>
                                ) : (
                                    <RawConfigEditor
                                        config={draftConfig}
                                        onChange={updateDraft}
                                        textareaClassName="h-60"
                                    />
                                )}
                            </div>
                        )}
                        {activeTab === "output" && (
                            <div className="p-3">
                                        {execution?.output ? (
                                            <JsonTree value={execution.output} defaultExpandDepth={2} />
                                        ) : (
                                            <p className="text-xs text-muted-foreground text-center py-4">
                                                {isTemplateUnit ? (
                                                    t("instance.templateNotExecutable")
                                                ) : (
                                                    t("definition.noOutputRun")
                                                )}
                                            </p>
                                        )}
                                {execution?.result && !execution.result.success && (
                                    <div className="mt-2 rounded-md bg-destructive/10 p-2 text-xs text-destructive">
                                        <div className="flex items-start gap-2">
                                            <span className="min-w-0 flex-1 break-words">
                                                {execution.result.message}
                                            </span>
                                            <button
                                                type="button"
                                                className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium text-destructive hover:bg-destructive/10"
                                                onClick={() => clearExecution(definition.id)}
                                            >
                                                <X size={10} className="inline-block mr-0.5" />
                                                {t("common.clear")}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {(canRunUnit ||
                        extensionUnitActions.length > 0) && (
                        <SecondaryActionsBar
                            actions={
                                canRunUnit
                                    ? plugin?.manifest.actions.filter(
                                          (a: ActionDeclaration) => !a.primary,
                                      ) ?? []
                                    : []
                            }
                            disabled={execution?.status === "running"}
                            onAction={(actionName) => {
                                void runAction(definition, actionName, definitions);
                            }}
                        >
                            {extensionUnitActions.length > 0 ? (
                                <ExtensionUnitConfigActions unit={unitConfigContext} />
                            ) : null}
                        </SecondaryActionsBar>
                    )}
                </div>
            ) : (
                <div className="flex-1 flex items-center justify-center p-4">
                    <div className="w-full max-w-[220px] rounded-xl border border-dashed border-border bg-muted/20 px-4 py-5 text-center">
                        <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-muted/50 text-muted-foreground">
                            <Puzzle size={18} className="opacity-70" />
                        </div>
                        <div className="text-xs font-medium text-foreground">
                            {t("sidebar.selectInstance")}
                        </div>
                        <div className="mt-1 text-[11px] leading-5 text-muted-foreground">
                            {t("sidebar.clickConfigButton")}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
